import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { GetClientOrders } from "Requests/Orders";

import validatePermissions from "Utils/validatePermissions";

import Input from "Components/Form/Input";
import SelectSearch from "Components/Form/SelectSearch";
import PageHeader from "Components/Pages/Header";
import PageWrapper from "Components/Pages/Wrapper";
import { Pagination } from "Components/Pagination";
import Breadcrumb from "Components/Shared/Breadcrumb";
import { SubOrdersModal } from "Components/Modals/SubOrdersModal";
import { Loader } from "Components/Loader";
import { useBudgetCombos } from "Hooks/useBudgetCombos";
import InputDateRange from "Components/Form/InputDateRange";
import { Button } from "Components/Button";
import moment from "moment";
import { Box } from "styles/globalComponents";
import { useQuery } from "react-query";
import { TableItems } from "./components/Table";
import { ChangeStatusModal } from "./components/ChangeStatusModal";
import { Form } from "./styles";

const types = [
  { id: "", name: "Tipo de busca" },
  { id: "name", name: "Nome" },
  { id: "email", name: "Email" },
  { id: "cpf", name: "CPF" },
  { id: "id_source", name: "Id de Origem" },
];

const PartnerOrders = () => {
  const user_permissions = useSelector((state) => state.User.permissions);

  const [filters, setFilters] = useState({
    typeSearch: "",
    value: "",
    client: "",
    start_date: undefined,
    end_date: undefined,
    page: 1,
  });

  const [filtersQuery, setFiltersQuery] = useState({
    typeSearch: "",
    value: "",
    client: "",
    start_date: undefined,
    end_date: undefined,
    page: 1,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subOrders, setSubOrders] = useState([]);
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const { data: budgetCombos, isLoading: loadingCombos } = useBudgetCombos();

  const statusOptions =
    budgetCombos?.status_sulamerica?.map((item) => ({
      id: item.id,
      name: item.status,
    })) || [];

  const openChangeStatusModal = (orderId) => {
    setSelectedOrderId(orderId);
    setIsChangeStatusModalOpen(true);
  };

  const closeChangeStatusModal = () => {
    setIsChangeStatusModalOpen(false);
    setSelectedOrderId(null);
  };

  const handleSort = (nextSortBy) => {
    if (sortBy === nextSortBy) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(nextSortBy);
    setSortDir("desc");
  };

  const clientOrders = useQuery({
    queryFn: () =>
      GetClientOrders({
        page: filtersQuery.page,
        home_collected: "1",
        end_date: filtersQuery.start_date?.format("YYYY-MM-DD") || undefined,
        start_date: filtersQuery.end_date?.format("YYYY-MM-DD") || undefined,
        client_id: filtersQuery.client.id,
        sort_by: sortBy,
        sort_dir: sortDir,
        ...(filtersQuery.typeSearch.id && {
          [filtersQuery.typeSearch.id]: filtersQuery.value,
        }),
      }),
    queryKey: ["client-orders", { ...filtersQuery, sortBy, sortDir }],
    refetchOnWindowFocus: false,
  });

  const blockSearchButton =
    !filters.client &&
    !filters.end_date &&
    !filters.start_date &&
    !filters.typeSearch &&
    !filters.value;

  function handleIntegration(order) {
    const {
      sub_orders,
      unit_id,
      motion_integration_status,
      order_type,
      order_id,
      home_collected,
    } = order;

    if (sub_orders.length > 0)
      return (
        <button
          type="button"
          onClick={() => {
            setSubOrders({ sub_orders, unit_id, home_collected });
            setIsModalOpen(true);
          }}
          className="button _blue _action"
        >
          Sub-Pedidos
        </button>
      );

    if (motion_integration_status === "Aguardando Envio") {
      const disabledButton = !validatePermissions(
        "Send Orders to Motion",
        user_permissions
      );

      return (
        <Link
          type="button"
          className="button _blue _action"
          to={
            disabledButton
              ? "#"
              : validatePermissions(
                  "Send Orders to Motion",
                  user_permissions
                ) && order_type === 5
              ? `/pedido/revisar/${order_id}`
              : `/pedidos-envio/${order_id}`
          }
          disabled={disabledButton}
        >
          Enviar
        </Link>
      );
    }

    if (motion_integration_status !== "Aguardando Envio")
      return order.motion_integration_status;

    return "Envio indisponível";
  }

  const handleDatesChange = (value) => {
    const updatedFilters = {
      ...filters,
    };

    if (value.startDate) {
      updatedFilters.start_date = value.startDate;
    }

    if (value.endDate) {
      updatedFilters.end_date = value.endDate;
    }

    setFilters(updatedFilters);
  };

  function handlePagination(e) {
    if (filters.page === e.currentPage) return;

    const clearedFilters = {
      ...filters,
      page: e.currentPage,
    };
    setFilters(clearedFilters);
    setFiltersQuery(clearedFilters);
  }

  function handleSubmit(event) {
    event.preventDefault();
    setFiltersQuery(filters);
  }

  const clearFilters = () => {
    const clearedFilters = {
      value: "",
      typeSearch: "",
      start_date: undefined,
      end_date: undefined,
      client: "",
      page: 1,
    };
    setFilters(clearedFilters);
    setFiltersQuery(clearedFilters);
  };

  const onSelectNextDay = async () => {
    const tomorrowDate = moment().add(1, "day");
    const afterTomorrowDate = moment().add(2, "day");

    const currentFiterSate = {
      value: "",
      typeSearch: "",
      start_date: tomorrowDate,
      end_date: afterTomorrowDate,
      client: "",
      page: 1,
    };

    setFilters(currentFiterSate);
    setFiltersQuery(currentFiterSate);
  };

  return (
    <PageWrapper className="entry-app-content">
      <div className="content-holder ">
        <div className="page-heading order-home-list">
          <div className="wrapper-header-content">
            <PageHeader title="Pedidos de parceiros">
              <Breadcrumb
                list={[
                  {
                    path: "#",
                    label: "Pedidos",
                    current: false,
                  },
                  {
                    path: "#",
                    label: "Todos os pedidos",
                    current: true,
                  },
                ]}
              />
            </PageHeader>
          </div>
          <div className="order-home-filter">
            <div className="filters-order">
              <Form className="select-search-submit" onSubmit={handleSubmit}>
                <div className="select-search">
                  <SelectSearch
                    id="calendars_id"
                    className="search"
                    type="text"
                    value={filters.typeSearch}
                    error=""
                    isMulti
                    isMoreSelect={false}
                    options={types}
                    loading={!types.length}
                    readOnly={!types.length}
                    valueKey="name"
                    labelKey="name"
                    placeholder="Tipo de busca"
                    onChange={(value) => {
                      if (value.id === "") return;
                      setFilters((prev) => ({ ...prev, typeSearch: value }));
                    }}
                  />
                </div>

                <div className="input-type">
                  <Input
                    readOnly={!filters.typeSearch}
                    placeholder="Buscar por nome, CPF, e e-mail ou ID do pedido"
                    value={filters.value}
                    onChange={({ target }) =>
                      setFilters((prev) => ({
                        ...prev,
                        value: target.value,
                      }))
                    }
                  />
                </div>

                <div className="select-search-submit-types client">
                  <SelectSearch
                    id="client_id"
                    type="text"
                    value={filters.client}
                    error=""
                    isMulti
                    isMoreSelect={false}
                    options={budgetCombos?.clients}
                    loading={!budgetCombos?.clients.length}
                    readOnly={!budgetCombos?.clients.length}
                    valueKey="id"
                    labelKey="name"
                    placeholder="Parceiro"
                    onChange={(value) => {
                      if (value.id === "") return;
                      setFilters((prev) => ({
                        ...prev,
                        client: value,
                      }));
                    }}
                  />
                </div>
                <div className="select-search-submit-types dates">
                  <InputDateRange
                    startDatePlaceholderText="Data inicial"
                    endDatePlaceholderText="Data final"
                    startDateId="start"
                    endDateId="end"
                    onDatesChange={(value) => handleDatesChange(value)}
                    openInModal={false}
                    dates={{
                      startDate: filters.start_date,
                      endDate: filters.end_date,
                    }}
                    blockPastChanges={false}
                    withClear={false}
                    error={undefined}
                    small={undefined}
                    purple={undefined}
                    label={undefined}
                  />
                </div>
                <Button onClick={onSelectNextDay}> D+1</Button>

                <Button
                  onClick={handleSubmit}
                  type="submit"
                  disabled={blockSearchButton}
                >
                  {" "}
                  Buscar{" "}
                </Button>
                <Button
                  outlined
                  onClick={clearFilters}
                  disabled={blockSearchButton}
                >
                  {" "}
                  Limpar filtros
                </Button>
              </Form>
            </div>
          </div>
        </div>
        {clientOrders?.data?.data.length >= 1 && (
          <TableItems
            orders={clientOrders.data.data}
            onOpenChangeStatus={openChangeStatusModal}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
          />
        )}

        {!clientOrders.isLoading && clientOrders?.data.data.length === 0 && (
          <h3 className="text-center text-purple">
            Nenhum resultado encontrado
          </h3>
        )}
        {clientOrders.isLoading && (
          <Box display="flex" mt="20" justify="center">
            <Loader />
          </Box>
        )}
      </div>
      <div className="pagination-wrapper">
        {!clientOrders.isLoading && (
          <>
            <Pagination
              totalRecords={clientOrders?.data?.meta?.pagination?.total_pages}
              currentPageSelected={
                clientOrders?.data?.meta?.pagination?.current_page
              }
              pageLimit={1}
              pageNeighbours={1}
              onPageChanged={(e) => handlePagination(e)}
            />
          </>
        )}
      </div>
      <SubOrdersModal
        modalVisible={isModalOpen}
        modalDismiss={() => setIsModalOpen(false)}
        handleClick={() => {}}
        title="Sub-Pedidos"
        userPermissions={user_permissions}
        subOrders={subOrders}
      />
      <ChangeStatusModal
        isOpen={isChangeStatusModalOpen}
        closeModal={closeChangeStatusModal}
        orderId={selectedOrderId}
        statusOptions={statusOptions}
      />
    </PageWrapper>
  );
};

export default PartnerOrders;
